<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="setting.isDiff"
    :isOutdated="isOutdated"
    :itemType="'SETTING'"
    :typename="'manatee_settings'"
    :item="settingComputed"
    :onUpdate="updateSetting"
    :onCreate="createSetting"
    :onDelete="deleteSetting"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
    v-if="$can('access', 'manatee_settings')"
  >
    <fieldset slot="fieldset" :disabled="isOutdated" ref="droptarget">
      <h4 class="ui horizontal left aligned divider header">
        {{ $t('SETTING_TARGET_HEADER') }}
      </h4>
      <div class="ui basic segment">
        <div class="ui form" :class="{ error: $v.setting.$error }">
          <div class="two fields">
            <div
              class="field"
              :class="{ error: $v.setting.forGroup.$error }"
              v-diff-field="settingDiff.forGroup"
            >
              <group-typeahead
                :allGroups="allGroups"
                :currentGroups="currentGroups"
                :multiple="false"
                @selected="selectGroup"
                @removed="removeGroup"
                :disabled="setting.isDiff || !allGroups"
              />
            </div>
            <div
              class="field"
              :class="{ error: $v.setting.forVersion.$error }"
              v-diff-field="settingDiff.forVersion"
            >
              <dropdown-select
                :labelText="$tc('VERSION', 1)"
                :items="availableVersions"
                @selected="selectVersion"
              />
            </div>
          </div>
          <div class="ui error message" v-if="!$v.setting.uniqueForGroupAndVersion">
            {{ $t('SETTING_GROUP_VERSION_NOT_UNIQUE') }}
          </div>
        </div>
      </div>
      <h4 class="ui horizontal left aligned divider header">
        {{ $t('SETTING_HEADER') }}
      </h4>
      <div class="ui basic segment">
        <div class="ui form" :class="{ error: $v.$error }">
          <div class="ui three column grid">
            <template v-for="(s, idx) in $v.settingsWithTemplates.$each.$iter">
              <div class="row" :key="idx + 'info_row'">
                <div class="seven wide column">
                  <div class="field" :class="{ error: s.key.$error }">
                    <typeahead
                      :items="templatesExceptAlreadyUsed"
                      :current-item="(s.$model.template && s.$model.template.key) || ''"
                      :label-text="
                        (s.$model.template && s.$model.template.description) || $t('SETTING_KEY')
                      "
                      :placeholder-text="$t('SETTING_KEY')"
                      :multiple="false"
                      @selected="selectTemplate(s.$model, $event)"
                    >
                      <template #dropdown="{ item }">
                        {{ item.key }}
                      </template>
                    </typeahead>
                  </div>
                </div>
                <div class="eight wide column">
                  <div
                    class="field"
                    :class="{ error: s.$error }"
                    v-diff-field.disable="settingDiff.settings[s.$model.key]"
                  >
                    <label :for="s.$model.key">{{ $t('SETTING_VALUE') }}</label>
                    <div
                      class="ui input"
                      v-if="s.$model.template && s.$model.template.type === 'int32'"
                    >
                      <input :id="s.$model.key" type="number" v-model.number="s.$model.value" />
                    </div>
                    <div
                      class="ui toggle checkbox"
                      v-else-if="s.$model.template && s.$model.template.type === 'boolean'"
                    >
                      <input
                        class="monospaced"
                        :id="s.$model.key"
                        type="checkbox"
                        v-model="s.$model.value"
                      />
                      <label :for="s.$model.key"> </label>
                    </div>
                    <div class="ui input" v-else>
                      <input
                        class="monospaced"
                        :id="s.$model.key"
                        type="text"
                        v-model="s.$model.value"
                      />
                    </div>
                  </div>
                </div>
                <div class="one wide column">
                  <div class="field">
                    <label for="delete-setting-btn">&nbsp;</label>
                    <div
                      id="delete-setting-btn"
                      class="ui mini circular red icon button"
                      @click="removeSetting(s.$model)"
                    >
                      <i class="delete icon" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="row" :key="idx + 'err_row'" v-if="s.$error">
                <div class="sixteen wide column">
                  <div class="ui icon error message">
                    <i class="exclamation icon" />
                    <div
                      v-if="
                        s.$model.template &&
                          s.$model.template.validation &&
                          s.$model.template.validation.errorMessage
                      "
                    >
                      {{ s.$model.template.validation.errorMessage }}
                    </div>
                    <div v-else-if="s.$model.template && s.$model.template.validation">
                      {{
                        $t('SETTING_INVALID_VALUE', {
                          instruction:
                            s.$model.template.validation.type == 'url'
                              ? $t('SETTING_INVALID_INSTRUCTIONS_URL')
                              : $t('SETTING_INVALID_INSTRUCTIONS_REGEXP', {
                                  regexp: s.$model.template.validation.regexp
                                })
                        })
                      }}
                    </div>
                    <div v-else-if="!s.validSettingValue">
                      {{
                        $t('SETTING_INVALID_VALUE', {
                          instruction: s.$model.template.type
                            ? $t('SETTING_INVALID_TYPED', { type: s.$model.template.type })
                            : ''
                        })
                      }}
                    </div>
                    <div v-else-if="!s.settingSupportedInVersion">
                      {{ $t('SETTING_NOT_SUPPORTED_IN_VERSION') }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="ui green right floated small labeled icon button" @click="addSetting">
          <i class="plus icon"></i>
          {{ $t('ADD', { thing: $tc('SETTING', 1) }) }}
        </div>
      </div>
    </fieldset>
    <div slot="rightBottomMenuSlot1" class="ui compact icon buttons" style="margin-right: 5px;">
      <button
        type="button"
        class="ui green button"
        @click="downloadIni()"
        :disabled="isOutdated || setting.isDiff"
      >
        <i class="download icon"></i>
      </button>
      <a ref="downloadLink" href="#" style="display: none;"></a>
      <input ref="uploadButton" type="file" accept=".ini" style="display: none;" />
      <button
        type="button"
        class="ui green button"
        @click="uploadIni()"
        :disabled="isOutdated || setting.isDiff"
      >
        <i class="upload icon"></i>
      </button>
    </div>
  </main-section>
  <div class="ui error message" v-else>
    $t('ACCESS_DENIED', { location: $t('ESTERO') })
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
</style>

<script>
// @flow
import Vue from 'vue'
import {
  Flow,
  Settings,
  SettingsSuggestions,
  Toast,
  Validation,
  ErrorCodes,
  UnsavedChecker,
  Group,
  File
} from 'services'
import { MainSection, RegexField, GroupTypeahead, Typeahead, DropdownSelect } from 'ui'
import { take } from 'rxjs/operators'
import { required, minLength } from 'vuelidate/lib/validators'

const defaultSetting = {
  ...Settings.defaultDescriptive
}
const validUrlRegexp = new RegExp(
  '^((https?|grpc):\\/\\/)?' + // protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
  '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
  'i'
) // fragment locator

// Validates a setting according to the information contained in the template.
const validSettingValue = s => {
  if (s.template) {
    if (s.template.validation) {
      try {
        const v = s.template.validation
        if (v.type === 'regex' && v.regexp) {
          const r = new RegExp(v.regexp)
          return r.test(s.value)
        }
        if (v.type === 'url') {
          if (!s.value && v.allowEmpty) return true
          return s.value !== '' && validUrlRegexp.test(s.value)
        }
      } catch (err) {
        console.warn('Failed to validate', s, err)
      }
    }
    // else we use the type information to validate
    switch (s.template.type) {
      case 'int32':
        return typeof s.value === 'number'
      case 'boolean':
        return s.value === true || s.value === false
      default:
        return true
    }
  }
  return true
}

// Validate that the setting is actually part of the templates
// This could happen if you change the version of an existing configuration.
function settingSupportedInVersion(s) {
  return _.find(this.templates, t => s.template && s.template.key === t.key) !== undefined
}

function uniqueForGroupAndVersion(s) {
  return (
    this.mode !== 'create' ||
    _.find(
      this.allSettings,
      other => other && s && other.forGroup === s.forGroup && other.forVersion === s.forVersion
    ) === undefined
  )
}

export default {
  name: 'setting',
  mixins: [UnsavedChecker.mixin],
  components: { MainSection, GroupTypeahead, Typeahead, DropdownSelect },
  props: ['settingId'],
  data() {
    return {
      mode: '',
      setting: { ...defaultSetting },
      availableVersions: [],
      templates: [],
      settings: [],
      isOutdated: false,
      settingDiff: { settings: {} }
    }
  },

  computed: {
    settingComputed() {
      return {
        ...this.setting,
        settings: _.reduce(
          this.settingsWithTemplates,
          (setting, s) => {
            setting[s.key] = s.value
            return setting
          },
          {}
        )
      }
    },
    templatesExceptAlreadyUsed() {
      return _.filter(
        this.templates,
        t =>
          _.find(
            this.settingsWithTemplates,
            other => other && other.template && other.template.key === t.key
          ) === undefined
      )
    },
    settingsWithTemplates() {
      return _.map(this.settings, s => {
        if (!s.template) s.template = _.find(this.templates, t => t.key === s.key)
        return s
      })
    },
    currentGroups() {
      const group = _.find(this.$store.state.group.items, g => g && g.id === this.setting.forGroup)
      return group ? [group] : group
    },
    allGroups() {
      return this.$store.state.group.items
    },
    allSettings() {
      return this.$store.state.manatee_settings.items
    },
    isEditMode() {
      return this.mode === 'edit'
    }
  },

  validations: {
    settingsWithTemplates: {
      $each: {
        validSettingValue,
        settingSupportedInVersion,
        key: {
          required,
          minLength: minLength(1)
        }
      }
    },
    setting: {
      uniqueForGroupAndVersion,
      forGroup: {
        required
      },
      forVersion: {
        required
      }
    }
  },

  methods: {
    processFile(file) {
      if (file.size > 2000000) {
        Toast.error(this.$i18n.t('ERR_FILE_TOO_LARGE', { max: '2MB' }))
        return
      }
      let r = new FileReader()
      r.onload = evt => {
        const newSettings = _.map(evt.target.result.split('\n'), line => {
          let [key, value] = line.split('=')
          const template = _.find(this.templates, t => t.key === key)
          // Coerce value into correct type
          switch (template.type) {
            case 'int32':
              value = parseInt(value, 10)
              break
            case 'boolean':
              value = value === 'true'
              break
          }
          return { key, value, template }
        })

        this.settings = newSettings
      }
      r.readAsText(file)
    },

    dropFunction(e) {
      if (e.dataTransfer.files.length === 1) {
        const file = e.dataTransfer.files[0]
        this.processFile(file)
      }
    },

    generateIni() {
      const ini = _.map(this.setting.settings, (value, key) => {
        return key + '=' + value
      })
      return ini.join('\n')
    },

    uploadIni() {
      this.$refs.uploadButton.click()
    },

    downloadIni() {
      File.downloadFile(
        this.generateIni(),
        `${_.head(this.currentGroups)?.name || this.setting.forGroup}-${
          this.setting.forVersion
        }.ini`,
        'text/plain',
        this.$refs.downloadLink
      )
    },

    removeSetting(s) {
      this.settings = _.filter(this.settings, other => other.key !== s.key)
      this.setting.settings = _.omit(this.setting.settings, [s.key])
    },

    selectTemplate(s, t) {
      s.key = t.key
      s.name = t.name
      // Initialize to default value (from the time of uploading settings meta)
      s.value = t.defaultValue
      this.$set(s, 'template', t)
    },
    addSetting() {
      if (_.find(this.settings, s => s.key === '')) return
      this.settings.push({ key: '', value: null })
    },
    selectVersion(version) {
      this.setting.forVersion = version?.id || version
      // Fetch templates for this version
      SettingsSuggestions.forVersion(this.setting.forVersion).then(
        templates => (this.templates = _.map(templates, t => ({ id: t.key, name: t.key, ...t })))
      )
    },
    selectGroup(group) {
      this.$set(this.setting, 'forGroup', group.id)
    },

    transferSettingsToObj() {
      const s = this.settingComputed
      return s.settings
    },

    removeGroup(group) {
      this.setting.forGroup = null
    },
    readSetting(settingId: string) {
      return Settings.readSetting(settingId, this.setting)
        .then(s => {
          this.setting = { ...s }

          // update the "display" list
          this.settings = _.map(this.setting.settings, (v, k) => ({
            key: k,
            value: v,
            template: _.find(this.templates, t => t.key === k)
          }))

          this.selectVersion(s.forVersion)
          this.isOutdated = false
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('SETTING', 1) })
            })
          )
          console.error(err)
        })
    },

    createSetting() {
      if (!this.$v.$invalid) {
        // Convert to proper settings
        this.setting.settings = this.transferSettingsToObj()

        return Settings.createSetting(this.setting)
          .then(res => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('SETTING', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'setting',
              params: { settingId: res.id }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SETTING', 1) })
              })
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_INCORRECT_INPUT', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SETTING', 1) })
          })
        )
        return Promise.reject('Validation failed')
      }
    },

    updateSetting() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          // Convert to proper settings
          this.setting.settings = this.transferSettingsToObj()
          return Settings.updateSetting(this.setting.id, this.setting)
            .then(res => {
              this.setting.version = res.version
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SETTING', 1) }),
                    thing: this.$i18n.tc('SETTING', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SETTING', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SETTING', 1) }),
              thing: this.$i18n.tc('SETTING', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_INCORRECT_INPUT', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SETTING', 1) })
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteSetting() {
      Settings.deleteSetting(this.setting.id)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('SETTING', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'settings'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('SETTING', 1) })
            })
          )
          console.error(err)
        })
    },
    editMode() {
      this.mode = 'edit'
      this.readSetting(this.settingId)
      this.settings = []
    },

    createMode() {
      this.mode = 'create'
      this.setting = { ...defaultSetting }
      const defaultVersion = _.last(this.availableVersions)
      if (defaultVersion) this.selectVersion(defaultVersion.id)
      this.settings = []
      this.addSetting()
    },
    onChangeChosen({ changedItem, fullDiff }) {
      this.setting = changedItem
      this.settingDiff = !_.isEmpty(fullDiff) ? fullDiff : { settings: {} }

      // We need this part bc the list is built first and then saved so it isn't present
      // on the model object before save.

      if (this.setting.isDiff) this.originalSettings = this.settings

      // update the "display" list
      this.settings = _.map(this.setting.settings, (v, k) => ({
        key: k,
        value: v,
        template: _.find(this.templates, t => t.key === k)
      }))

      this.selectVersion(this.setting.forVersion)
    },
    onRevert(s) {
      this.setting = s
      this.settingDiff = {}
      this.setttings = this.originalSettings
      this.selectVersion(this.setting.forVersion)
    }
  },

  mounted() {
    // Populate the versions dropdown
    SettingsSuggestions.getVersions('settings').then(versions => {
      // Only one version, set this
      if (versions && versions.length === 1 && !this.setting.forVersion) {
        this.selectVersion(versions[0])
      }
      this.availableVersions = _.map(versions, v => {
        return { id: v, name: v }
      })
    })

    if (this.settingId) {
      this.editMode()
    } else {
      this.createMode()
    }

    Vue.nextTick(() => {
      if (this.$refs.droptarget)
        File.addDropFileFunctionalityToDroptarget(this.$refs.droptarget, this.dropFunction)

      this.$refs.uploadButton.addEventListener('change', e => {
        this.processFile(e.target.files[0])
      })
    })
  },

  watch: {
    settingId(newSettingId) {
      this.$v.$reset()
      if (newSettingId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
</script>
